import React, {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	ConvoAIResponseState,
	type ConvoAIStreamError,
	type ConvoAIStreamingState,
	useConvoAiStreamingLazy,
	type UserConvoAIStreamingConfig,
} from '@atlassian/ai-agent-streaming';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import type { AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest } from '@atlassian/convo-ai-api';

import { useProductDependencies } from '../../../../controllers/use-product-dependencies';

import { messages } from './messages';

const buttonContainerStyles = xcss({
	border: `${token('border.width')} solid ${token('color.border')}`,
	width: 'fit-content',
	marginTop: 'space.075',
	marginBottom: 'space.075',
	borderRadius: 'radius.large',
});

export type SummaryRequestBuilderType = {
	contentAri: string;
	locale: string;
};

export const CC_SMARTS_STREAM_BASE_URL = '/gateway/api/ai';

export const buildSummaryRequest = ({
	contentAri,
	locale,
}: SummaryRequestBuilderType): [
	AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest,
	UserConvoAIStreamingConfig,
] => {
	return [
		{
			ai_feature_input: {
				content_ari: contentAri,
				locale,
				character_max: 95,
			},
		},
		{
			apiPath: 'v2/ai-feature/confluence-concise-summary',
			baseUrl: CC_SMARTS_STREAM_BASE_URL,
			delayChunkUpdate: { gap: 10, time: 20 },
			headers: {
				'x-experience-id': 'confluence-concise-summary',
				'x-product': 'confluence',
			},
		},
	];
};

type AIDescriptionButtonProps = {
	analyticsSource: string;
	confluencePageId: string;
	isAILoading?: boolean;
	pageAri: string;
	setAIDescriptionContent: Dispatch<SetStateAction<string | undefined | null>>;
	setAIError: Dispatch<SetStateAction<ConvoAIStreamError | undefined>>;
	setAIResponseState: Dispatch<SetStateAction<ConvoAIResponseState | undefined>>;
	setIsAILoading: Dispatch<SetStateAction<boolean | undefined>>;
};

type AIDescriptionStreamResponse = {
	content: string;
};

export const AIDescriptionButton = ({
	pageAri,
	confluencePageId,
	isAILoading,
	setIsAILoading,
	setAIError,
	setAIDescriptionContent,
	setAIResponseState,
	analyticsSource,
}: AIDescriptionButtonProps): React.JSX.Element => {
	const { aiDescriptionExperienceTracker } = useProductDependencies();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { locale } = useIntl();

	const { trackAIInteractionInit, trackAIResultView, trackAIResultError } =
		useAIEventsInstrumentation();
	const onStateChange = useCallback(
		(state: ConvoAIStreamingState<AIDescriptionStreamResponse>) => {
			switch (state.responseState) {
				case ConvoAIResponseState.Error:
					if (state.error) {
						const { status_code: aiErrorCode, message_template: aiErrorMessage } =
							state.error.message;
						trackAIResultError({
							aiErrorCode,
							aiErrorMessage,
						});
					}
					break;
				case ConvoAIResponseState.FinalResponse:
					if (state.content) {
						trackAIResultView();
					}
					break;
				default:
					break;
			}
		},
		[trackAIResultError, trackAIResultView],
	);
	// `fetchNonce` is a monotonically increasing per-click token: it changes on *every*
	// click, so the fetching effect below always re-fires (e.g. when retrying after an
	// error). Bumping it in the click handler is what schedules a fetch.
	const [fetchNonce, setFetchNonce] = useState(0);

	const [request, config] = useMemo(
		() =>
			buildSummaryRequest({
				contentAri: pageAri,
				locale,
			}),
		[locale, pageAri],
	);

	const [{ content, responseState, error }, fetchAI] = useConvoAiStreamingLazy<
		AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest,
		AIDescriptionStreamResponse
	>(config, onStateChange);

	// This effect runs after a click has bumped `fetchNonce`, then fires the request.
	// `fetchNonce > 0` skips the initial mount; keying the effect on `fetchNonce`
	// guarantees it re-runs on every click.
	useEffect(() => {
		if (fetchNonce > 0) {
			// oxlint-disable-next-line react-hooks/exhaustive-deps
			fetchAI(request);
		}
		// fetchNonce so repeated clicks still trigger a fetch.
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchNonce]);

	const startAIDescription = useCallback(() => {
		trackAIInteractionInit();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiDescription',
				source: analyticsSource,
			},
		}).fire();
		aiDescriptionExperienceTracker?.start({ analyticsSource, confluencePageId });

		setIsAILoading(true);
		// Bump `fetchNonce` so the fetching effect re-fires even on repeat clicks/retries.
		setFetchNonce((prev) => prev + 1);
	}, [
		trackAIInteractionInit,
		confluencePageId,
		createAnalyticsEvent,
		aiDescriptionExperienceTracker,
		analyticsSource,
		setIsAILoading,
	]);

	useEffect(() => {
		setAIResponseState(responseState);
		setAIDescriptionContent(content);
		setAIError(error);
	}, [responseState, content, error, setAIResponseState, setAIDescriptionContent, setAIError]);

	return (
		<Box xcss={buttonContainerStyles}>
			<Button
				appearance="subtle"
				onClick={startAIDescription}
				isDisabled={isAILoading}
				iconBefore={AtlassianIntelligenceIcon}
			>
				<FormattedMessage {...messages.suggestDescriptionLabel} />
			</Button>
		</Box>
	);
};
